/* ── IMAGE HOTSPOTS ───
   Pulsating call-outs on interior shots. Two jobs:

   1. PLACE the dot. Coordinates are stored as a percentage of the image's OWN
      pixels, never of the box it is displayed in. Every host here draws with
      object-fit:cover — .colhero is aspect-ratio 2560/1100, .scene is 4/3.3 —
      so the visible crop changes with the viewport, and a dot positioned at a
      raw left:42%/top:61% of the CONTAINER slides off the chair it points at
      the moment the box reshapes. mapPoint() runs the cover transform so the
      dot tracks the picture instead of the frame. object-position is READ,
      not assumed: .colhero .bg img switches to `right center` under 1080px.

   2. RESOLVE the product. A hotspot carries a pinned `handle` (optionally with
      a `variant`) or just a search term. Liquid resolves what it can at render
      (see snippets/image-hotspots.liquid); anything left over is resolved here
      against /products/<handle>.js or /search/suggest.json, both of which only
      ever answer for products live on the Online Store — so a product that is
      drafted, archived or renamed stops appearing on its own rather than
      leaving a dead link on a lifestyle shot. An unpinned term resolves to a
      product only when the title carries every word of it; otherwise the dot
      says so instead of opening whatever ranked first.

   A dot whose point is cropped out of view is hidden — an edge-pinned dot
   pointing at nothing off-frame is worse than no dot.
   ─── */

(function () {
  'use strict';

  var CACHE_PREFIX = 'qhs:';
  var open = null; // the one open bubble, document-wide

  /* ── product lookup ─── */

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cacheGet(key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null; // private mode, blocked storage — just skip the cache
    }
  }

  function cacheSet(key, value) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
    } catch (e) {
      /* quota or blocked — the lookup still worked, it just won't be reused */
    }
  }

  // Shopify's predictive search. Published products only, so "is it live" is
  // answered by the endpoint rather than by anything we have to maintain.
  function search(term) {
    var key = term.toLowerCase();
    var hit = cacheGet(key);
    if (hit) return Promise.resolve(hit);

    var url =
      root().replace(/\/$/, '') +
      '/search/suggest.json?q=' +
      encodeURIComponent(term) +
      '&resources[type]=product&resources[limit]=4';

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var items =
          (data && data.resources && data.resources.results && data.resources.results.products) ||
          [];
        var best = pick(items, term);
        if (best) cacheSet(key, best);
        return best;
      });
  }

  // Resolve a handle directly. The settings page passes its markers as a raw
  // JSON string (theme-editor block settings, and Liquid has no JSON parser),
  // so Liquid never gets to resolve `handle` there the way it does for a
  // metafield array — this is that path's equivalent. It matters because
  // titles are NOT unique on this catalogue: `okse-v1`/`okse-v3` are both
  // "Okse" and `tulip-stool`/`tulip-stool-premium-…` are both "Tulip Stool",
  // so a search term alone picks whichever the search ranked first.
  //
  // `variantId` pins the colourway as well, for a shot that shows one product
  // twice in two finishes. An id that no longer exists falls back to the
  // product, never to a ?variant= the storefront would ignore.
  //
  // /products/<handle>.js 404s for a drafted, archived or renamed product, so
  // it carries the same "is it live" guarantee as predictive search, and the
  // caller falls back to `q` when it does.
  function byHandle(handle, variantId) {
    var key = 'h:' + handle + (variantId ? ':' + variantId : '');
    var hit = cacheGet(key);
    if (hit) return Promise.resolve(hit);

    var base = root().replace(/\/$/, '');
    return fetch(base + '/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p || !p.handle) return null;
        var url = base + '/products/' + p.handle;
        // The AJAX API gives featured_image as a plain URL string on the
        // product, and an image OBJECT on the variant.
        var image = p.featured_image || (p.images && p.images[0]) || null;
        var pinned = variantId ? findVariant(p, variantId) : null;
        if (pinned) {
          url += '?variant=' + pinned.id;
          if (pinned.featured_image && pinned.featured_image.src) image = pinned.featured_image.src;
        }
        var out = { title: p.title, url: url, image: image, type: p.type || '' };
        cacheSet(key, out);
        return out;
      })
      .catch(function () {
        return null;
      });
  }

  function findVariant(product, variantId) {
    var want = String(variantId);
    var list = product.variants || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === want) return list[i];
    }
    return null;
  }

  // Prefer an exact title match, then a title that starts with the term, then
  // the first result whose title carries every word of it. Predictive search
  // is fuzzy enough that "LinForm table" can rank a stool above the table it
  // names, and "accent chair" ranks a lounge chair first — so a result that
  // matches nothing in the term is NOT used. A dot that says it found nothing
  // is recoverable; one that opens a plausible-looking wrong product is not,
  // because nobody reports it. Pin `handle` when it has to be exact.
  function pick(items, term) {
    if (!items.length) return null;
    var t = term.trim().toLowerCase();
    var words = t.split(/\s+/).filter(Boolean);
    var exact = null;
    var starts = null;
    var all = null;

    for (var i = 0; i < items.length; i++) {
      var title = (items[i].title || '').toLowerCase();
      if (!exact && title === t) exact = items[i];
      if (!starts && title.indexOf(t) === 0) starts = items[i];
      if (!all && hasEveryWord(title, words)) all = items[i];
    }

    var chosen = exact || starts || all;
    if (!chosen) return null;
    return {
      title: chosen.title,
      url: chosen.url,
      image: chosen.featured_image ? chosen.featured_image.url : null,
      type: chosen.type || ''
    };
  }

  function hasEveryWord(title, words) {
    if (!words.length) return false;
    for (var i = 0; i < words.length; i++) {
      if (title.indexOf(words[i]) === -1) return false;
    }
    return true;
  }

  /* ── geometry ─── */

  // Where object-position puts the drawn image, as a 0–1 pair. Percentages and
  // the keywords the theme actually uses; anything exotic falls back to centre.
  function originOf(img) {
    var raw = (getComputedStyle(img).objectPosition || '50% 50%').trim().split(/\s+/);
    return [axis(raw[0]), axis(raw[1] === undefined ? raw[0] : raw[1])];
  }

  function axis(token) {
    if (!token) return 0.5;
    if (token === 'left' || token === 'top') return 0;
    if (token === 'right' || token === 'bottom') return 1;
    if (token === 'center') return 0.5;
    var pct = parseFloat(token);
    if (isNaN(pct)) return 0.5;
    if (token.indexOf('%') > -1) return pct / 100;
    return 0.5; // a px offset — not used here, and guessing would misplace dots
  }

  // Map a point expressed in percent of the image's own pixels into offsets
  // within the displayed frame. Returns null when the point is cropped away.
  function mapPoint(img, xPct, yPct) {
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    var fw = img.clientWidth;
    var fh = img.clientHeight;
    if (!nw || !nh || !fw || !fh) return null;

    var fit = getComputedStyle(img).objectFit || 'cover';
    var scale =
      fit === 'contain'
        ? Math.min(fw / nw, fh / nh)
        : fit === 'fill'
          ? null
          : Math.max(fw / nw, fh / nh);

    var dw, dh, ox, oy;
    if (scale === null) {
      dw = fw;
      dh = fh;
      ox = 0;
      oy = 0;
    } else {
      dw = nw * scale;
      dh = nh * scale;
      var origin = originOf(img);
      ox = (fw - dw) * origin[0];
      oy = (fh - dh) * origin[1];
    }

    var x = ox + (xPct / 100) * dw;
    var y = oy + (yPct / 100) * dh;

    // Cropped out of the visible frame — caller hides the dot.
    if (x < 0 || y < 0 || x > fw || y > fh) return null;
    return { x: x, y: y };
  }

  /* ── bubble ─── */

  function closeOpen() {
    if (!open) return;
    open.bubble.hidden = true;
    open.bubble.removeAttribute('data-open');
    open.dot.setAttribute('aria-expanded', 'false');
    open = null;
  }

  function fill(bubble, product) {
    bubble.className = 'qhs-bubble';
    bubble.href = product.url;
    bubble.innerHTML = '';

    if (product.image) {
      var th = document.createElement('div');
      th.className = 'qhs-th';
      var img = document.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.loading = 'lazy';
      th.appendChild(img);
      bubble.appendChild(th);
    }

    var main = document.createElement('div');
    main.className = 'qhs-main';

    var title = document.createElement('div');
    title.className = 'qhs-title';
    title.textContent = product.title;
    main.appendChild(title);

    if (product.label || product.type) {
      var meta = document.createElement('div');
      meta.className = 'qhs-meta';
      meta.textContent = product.label || product.type;
      main.appendChild(meta);
    }

    var go = document.createElement('span');
    go.className = 'qhs-go';
    go.textContent = bubble.dataset.cta || 'View product →';
    main.appendChild(go);

    bubble.appendChild(main);
  }

  function message(bubble, text, state) {
    bubble.className = 'qhs-bubble ' + state;
    bubble.removeAttribute('href');
    bubble.innerHTML = '';
    var main = document.createElement('div');
    main.className = 'qhs-main';
    var title = document.createElement('div');
    title.className = 'qhs-title';
    title.textContent = text;
    main.appendChild(title);
    bubble.appendChild(main);
  }

  // Sit the bubble beside the dot and keep every edge inside the host, which
  // clips (overflow:hidden on .colhero / .scene / .pf-media).
  function place(layer, dot, bubble) {
    var lw = layer.clientWidth;
    var lh = layer.clientHeight;
    var dx = parseFloat(dot.style.left);
    var dy = parseFloat(dot.style.top);

    bubble.hidden = false;
    var bw = bubble.offsetWidth;
    var bh = bubble.offsetHeight;
    var gap = 18;
    var pad = 8;

    // Prefer right of the dot; flip left when it would overhang.
    var x = dx + gap;
    if (x + bw > lw - pad) x = dx - gap - bw;
    x = Math.max(pad, Math.min(x, lw - bw - pad));

    var y = dy - bh / 2;
    y = Math.max(pad, Math.min(y, lh - bh - pad));

    bubble.style.left = x + 'px';
    bubble.style.top = y + 'px';
  }

  function activate(layer, dot, bubble, spot) {
    if (open && open.dot === dot) {
      closeOpen();
      return;
    }
    closeOpen();

    dot.setAttribute('aria-expanded', 'true');
    open = { dot: dot, bubble: bubble };

    if (spot.resolved) {
      fill(bubble, spot.resolved);
      place(layer, dot, bubble);
      requestAnimationFrame(function () {
        bubble.setAttribute('data-open', '');
      });
      return;
    }

    message(bubble, bubble.dataset.finding || 'Finding this product…', 'is-loading');
    place(layer, dot, bubble);
    requestAnimationFrame(function () {
      bubble.setAttribute('data-open', '');
    });

    // Handle first when one is given, search term as the fallback.
    var lookup = spot.handle
      ? byHandle(spot.handle, spot.variant).then(function (p) {
          return p || (spot.q ? search(spot.q) : null);
        })
      : search(spot.q);

    lookup
      .then(function (product) {
        if (!open || open.dot !== dot) return; // reader moved on
        if (!product) {
          message(bubble, bubble.dataset.noMatch || 'No matching product is live right now.', 'is-empty');
        } else {
          if (spot.label) product.label = spot.label;
          spot.resolved = product;
          fill(bubble, product);
        }
        place(layer, dot, bubble);
      })
      .catch(function () {
        if (!open || open.dot !== dot) return;
        message(bubble, bubble.dataset.loadError || 'Could not load this product.', 'is-empty');
        place(layer, dot, bubble);
      });
  }

  /* ── mount ─── */

  function mount(layer) {
    if (layer.dataset.qhsMounted) return;
    layer.dataset.qhsMounted = '1';

    var host = layer.parentElement;
    var img = host && host.querySelector('img');
    var spots;
    try {
      spots = JSON.parse(layer.dataset.hotspots || '[]');
    } catch (e) {
      return; // malformed JSON in the metafield — render the image plain
    }
    if (!img || !spots.length) return;

    var dots = [];

    spots.forEach(function (spot, i) {
      if (typeof spot.x !== 'number' || typeof spot.y !== 'number') return;
      if (!spot.q && !spot.handle && !spot.resolved) return;

      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'qhs-dot';
      dot.setAttribute('aria-expanded', 'false');
      dot.setAttribute(
        'aria-label',
        spot.resolved ? spot.resolved.title : spot.label || spot.q || spot.handle || 'Product call-out'
      );

      var bubble = document.createElement('a');
      bubble.className = 'qhs-bubble';
      bubble.hidden = true;
      if (layer.dataset.cta) bubble.dataset.cta = layer.dataset.cta;
      ['finding', 'noMatch', 'loadError'].forEach(function (k) {
        if (layer.dataset[k]) bubble.dataset[k] = layer.dataset[k];
      });

      // Click, never hover — hover never fires on touch, and these pages are
      // touched. Same rule as the product gallery's action bar.
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        activate(layer, dot, bubble, spot);
      });

      layer.appendChild(dot);
      layer.appendChild(bubble);
      dots.push({ dot: dot, spot: spot, bubble: bubble });
    });

    function reposition() {
      dots.forEach(function (d) {
        var p = mapPoint(img, d.spot.x, d.spot.y);
        if (!p) {
          d.dot.hidden = true;
          if (open && open.dot === d.dot) closeOpen();
          return;
        }
        d.dot.hidden = false;
        d.dot.style.left = p.x + 'px';
        d.dot.style.top = p.y + 'px';
      });
      if (open && open.bubble.parentElement === layer) {
        place(layer, open.dot, open.bubble);
      }
      layer.setAttribute('data-qhs-ready', '');
    }

    if (img.complete && img.naturalWidth) {
      reposition();
    } else {
      img.addEventListener('load', reposition, { once: true });
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(reposition).observe(img);
    } else {
      window.addEventListener('resize', reposition);
    }
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll('[data-qhs]').forEach(mount);
  }

  document.addEventListener('click', function (e) {
    if (open && !e.target.closest('.qhs-bubble') && !e.target.closest('.qhs-dot')) closeOpen();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && open) {
      var dot = open.dot;
      closeOpen();
      dot.focus();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  // Theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    mountAll(e.target);
  });
})();
